import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { 
  Activity, 
  CheckCircle2, 
  Clock3, 
  Filter, 
  Loader2, 
  PlayCircle, 
  Search, 
  Sparkles, 
  XCircle,
  Zap,
  Timer
} from "lucide-react";

export default function AutomationIntelligence({ darkMode }) {
  const { currentUser } = useSelector((state) => state.auth);
  
  const [runs, setRuns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Constants for ROI Calculation
  const MANUAL_TEST_SECONDS = 300; // Assuming 5 minutes (300s) for a human QA to run and document a test

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.org_id) return;
      setLoading(true);
      try {
        const projectsRes = await axios.get(`https://api.testoraglobal.in/api/project/org/${currentUser.org_id}`);
        const projectPayload = projectsRes.data?.success 
          ? projectsRes.data.projects 
          : (Array.isArray(projectsRes.data) ? projectsRes.data : []);

        const historyPromises = projectPayload.map(p => 
          axios.get(`https://api.testoraglobal.in/api/history/${p.project_id}`).catch(() => ({ data: [] }))
        );
        const historyResponses = await Promise.all(historyPromises);

        let allHistory = [];
        historyResponses.forEach(res => {
          if (Array.isArray(res.data)) {
            allHistory = [...allHistory, ...res.data];
          }
        });

        allHistory.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

        setProjects(projectPayload);
        setRuns(allHistory);
      } catch (error) {
        console.error("Dashboard Fetch Error:", error);
        setRuns([]);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // Dynamic Data Aggregation & ROI Math Engine
  const report = useMemo(() => {
    const projectMap = {};
    projects.forEach(p => { projectMap[p.project_id] = p; });

    const filteredByProject = selectedProjectId === "all" 
      ? runs 
      : runs.filter(run => String(run.project_id) === String(selectedProjectId));

    const totalRuns = filteredByProject.length;
    const passedRuns = filteredByProject.filter(r => r.status === 'Pass').length;
    const failedRuns = totalRuns - passedRuns;
    const successRate = totalRuns > 0 ? Math.round((passedRuns / totalRuns) * 100) : 0;

    // Fix for NaN: Robustly strip out any letters (like 's') and parse safely
    let totalSeconds = 0;
    filteredByProject.forEach(run => {
      const rawVal = String(run.duration || "0");
      const parsed = parseFloat(rawVal.replace(/[^\d.-]/g, ''));
      totalSeconds += isNaN(parsed) ? 0 : parsed;
    });

    const avgSecs = totalRuns > 0 ? (totalSeconds / totalRuns).toFixed(1) : 0;
    const averageDuration = `${avgSecs}s`;

    // --- ROI CALCULATION ---
    const totalManualSeconds = totalRuns * MANUAL_TEST_SECONDS;
    const secondsSaved = Math.max(0, totalManualSeconds - totalSeconds);
    
    const hoursSaved = Math.floor(secondsSaved / 3600);
    const minutesSaved = Math.floor((secondsSaved % 3600) / 60);
    const formattedTimeSaved = hoursSaved > 0 ? `${hoursSaved}h ${minutesSaved}m` : `${minutesSaved}m`;
    
    // Efficiency Multiplier (e.g., Testora is "50x Faster" than a human)
    const efficiencyMultiplier = totalSeconds > 0 ? Math.round(totalManualSeconds / totalSeconds) : 0;

    const latestRun = filteredByProject[0] || null;

    const breakdownMap = {};
    filteredByProject.forEach(run => {
      const pid = String(run.project_id);
      if (!breakdownMap[pid]) {
        breakdownMap[pid] = {
          projectId: pid,
          projectName: projectMap[pid]?.project_name || pid,
          total: 0,
          passed: 0,
          failed: 0
        };
      }
      breakdownMap[pid].total += 1;
      if (run.status === 'Pass') breakdownMap[pid].passed += 1;
      else breakdownMap[pid].failed += 1;
    });

    const projectBreakdown = Object.values(breakdownMap).map(b => ({
      ...b,
      successRate: b.total > 0 ? Math.round((b.passed / b.total) * 100) : 0,
      passedRuns: b.passed,
      failedRuns: b.failed
    })).sort((a, b) => b.total - a.total); 

    return { 
      totalRuns, passedRuns, failedRuns, successRate, averageDuration, 
      formattedTimeSaved, efficiencyMultiplier,
      latestRun, projectBreakdown, filteredRuns: filteredByProject, projectMap 
    };
  }, [runs, projects, selectedProjectId]);

  const finalFilteredRuns = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return report.filteredRuns;

    return report.filteredRuns.filter((run) => {
      const projectName = report.projectMap[String(run.project_id)]?.project_name || "";
      return [run.title, run.target_url, projectName, run.status, run.duration]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [report.filteredRuns, report.projectMap, searchQuery]);

  const borderColor = darkMode ? "border-gray-800/70" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#0b1220]" : "bg-white";

  return (
    <div className={`min-h-screen ${darkMode ? "bg-[#020617] text-white" : "bg-slate-50 text-slate-900"} p-6 lg:p-8`}>
      <div className="mx-auto max-w-7xl space-y-6">
        
        {/* TOP BAR / FILTERS */}
        <div className={`rounded-3xl border ${borderColor} ${cardBg} p-6 shadow-xl`}> 
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-purple-500/10 px-3 py-1 text-sm font-semibold text-purple-400">
                <Sparkles size={16} /> Automation Intelligence
              </div>
              <h1 className="text-3xl font-bold tracking-tight">Automation Execution Intelligence</h1>
              <p className={`mt-2 max-w-2xl text-sm ${darkMode ? "text-gray-400" : "text-slate-600"}`}>
                Review recent automation runs, track reliability by project, and monitor hours saved vs manual QA.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <label className={`flex items-center gap-2 rounded-xl border ${borderColor} px-3 py-2 ${darkMode ? "bg-[#111827]" : "bg-slate-100"}`}>
                <Filter size={16} className="text-purple-400" />
                <select
                  value={selectedProjectId}
                  onChange={(event) => setSelectedProjectId(event.target.value)}
                  className={`bg-transparent text-sm outline-none ${darkMode ? "text-white" : "text-slate-800"}`}
                >
                  <option value="all">All projects</option>
                  {projects.map((project) => (
                    <option key={project.project_id} value={project.project_id}>
                      {project.project_name}
                    </option>
                  ))}
                </select>
              </label>

              <label className={`flex items-center gap-2 rounded-xl border ${borderColor} px-3 py-2 ${darkMode ? "bg-[#111827]" : "bg-slate-100"}`}>
                <Search size={16} className="text-purple-400" />
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search runs"
                  className={`bg-transparent text-sm outline-none ${darkMode ? "text-white" : "text-slate-800"}`}
                />
              </label>
            </div>
          </div>
        </div>

        {/* CONTENT AREA */}
        {loading ? (
          <div className={`flex min-h-56 items-center justify-center rounded-3xl border ${borderColor} ${cardBg}`}>
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : (
          <>
            {/* KPI METRICS */}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className={`rounded-2xl border ${borderColor} ${cardBg} p-5 shadow-sm flex flex-col justify-between`}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm ${darkMode ? "text-gray-400" : "text-slate-500"}`}>Total Executions</p>
                  <PlayCircle className="text-blue-400" size={18} />
                </div>
                <p className="mt-4 text-3xl font-semibold">{report.totalRuns}</p>
              </div>
              
              <div className={`rounded-2xl border ${borderColor} ${cardBg} p-5 shadow-sm flex flex-col justify-between`}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm ${darkMode ? "text-gray-400" : "text-slate-500"}`}>Success Rate</p>
                  <CheckCircle2 className="text-emerald-400" size={18} />
                </div>
                <p className="mt-4 text-3xl font-semibold">{report.successRate}%</p>
              </div>
              
              <div className={`rounded-2xl border ${borderColor} ${cardBg} p-5 shadow-sm flex flex-col justify-between`}>
                <div className="flex items-center justify-between">
                  <p className={`text-sm ${darkMode ? "text-gray-400" : "text-slate-500"}`}>Avg. Duration</p>
                  <Clock3 className="text-amber-400" size={18} />
                </div>
                <p className="mt-4 text-3xl font-semibold">{report.averageDuration}</p>
              </div>

              {/* ROI CARD - HIGH VISIBILITY */}
              <div className={`rounded-2xl border ${darkMode ? "border-purple-500/50" : "border-purple-300"} ${darkMode ? "bg-purple-900/10" : "bg-purple-50"} p-5 shadow-lg flex flex-col justify-between relative overflow-hidden`}>
                <div className="absolute -right-4 -top-4 opacity-10">
                  <Timer size={100} className="text-purple-500" />
                </div>
                <div className="flex items-center justify-between relative z-10">
                  <p className={`text-sm font-bold ${darkMode ? "text-purple-300" : "text-purple-800"}`}>QA Time Saved</p>
                  <Zap className="text-yellow-400" fill="currentColor" size={18} />
                </div>
                <div className="relative z-10 flex items-end justify-between mt-4">
                  <p className="text-3xl font-semibold text-purple-500">{report.formattedTimeSaved}</p>
                  {report.efficiencyMultiplier > 0 && (
                    <span className="text-[10px] font-bold bg-yellow-400/20 text-yellow-600 dark:text-yellow-400 px-2 py-1 rounded-md border border-yellow-400/30 whitespace-nowrap">
                      ⚡ {report.efficiencyMultiplier}x FASTER
                    </span>
                  )}
                </div>
                <p className={`text-[10px] mt-2 relative z-10 ${darkMode ? "text-purple-400/60" : "text-purple-600/60"}`}>*Based on 5m avg manual test</p>
              </div>
            </div>

            {/* DASHBOARD CHARTS/DATA */}
            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              {/* LATEST RUN SUMMARY */}
              <div className={`rounded-3xl border ${borderColor} ${cardBg} p-6 shadow-sm`}>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Latest automation run</h2>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-slate-500"}`}>Most recent execution snapshot</p>
                  </div>
                  <div className={`rounded-full px-3 py-1 text-xs font-semibold ${report.latestRun?.status === "Pass" ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}`}>
                    {report.latestRun?.status || "No runs"}
                  </div>
                </div>

                {report.latestRun ? (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-purple-500/20 bg-purple-500/10 p-4">
                      <p className="text-sm font-semibold">{report.latestRun.title || "Automation test"}</p>
                      <p className={`mt-1 text-sm ${darkMode ? "text-gray-300" : "text-slate-600"}`}>{report.latestRun.target_url}</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className={`rounded-2xl border ${borderColor} p-4`}>
                        <p className={`text-xs uppercase tracking-[0.2em] ${darkMode ? "text-gray-500" : "text-slate-400"}`}>Project</p>
                        <p className="mt-2 font-semibold truncate" title={report.projectMap[String(report.latestRun.project_id)]?.project_name || report.latestRun.project_id || "Unknown"}>
                          {report.projectMap[String(report.latestRun.project_id)]?.project_name || report.latestRun.project_id || "Unknown"}
                        </p>
                      </div>
                      <div className={`rounded-2xl border ${borderColor} p-4`}>
                        <p className={`text-xs uppercase tracking-[0.2em] ${darkMode ? "text-gray-500" : "text-slate-400"}`}>Duration</p>
                        <p className="mt-2 font-semibold">{report.latestRun.duration || "0s"}</p>
                      </div>
                      <div className={`rounded-2xl border ${borderColor} p-4`}>
                        <p className={`text-xs uppercase tracking-[0.2em] ${darkMode ? "text-gray-500" : "text-slate-400"}`}>Executed</p>
                        <p className="mt-2 font-semibold">{report.latestRun.created_at ? new Date(report.latestRun.created_at).toLocaleString() : "Unknown"}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`flex min-h-45 items-center justify-center rounded-2xl border border-dashed ${borderColor} ${darkMode ? "text-gray-500" : "text-slate-500"}`}>
                    <div className="text-center p-8">
                      <Activity size={28} className="mx-auto mb-2" />
                      <p>No automation runs recorded yet.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* PROJECT HEALTH BREAKDOWN */}
              <div className={`rounded-3xl border ${borderColor} ${cardBg} p-6 shadow-sm flex flex-col h-full`}>
                <h2 className="text-lg font-semibold">Project-wise health</h2>
                <p className={`mt-1 text-sm ${darkMode ? "text-gray-400" : "text-slate-500"}`}>Filtered execution outcome by project</p>

                <div className="mt-5 space-y-3 flex-1 overflow-y-auto max-h-[300px] custom-scrollbar pr-2">
                  {report.projectBreakdown.length === 0 ? (
                    <div className={`rounded-2xl border border-dashed ${borderColor} p-4 text-center text-sm flex items-center justify-center h-full ${darkMode ? "text-gray-500" : "text-slate-500"}`}>
                      No project data available.
                    </div>
                  ) : (
                    report.projectBreakdown.map((item) => (
                      <div key={item.projectId} className={`rounded-2xl border ${borderColor} p-4`}>
                        <div className="flex items-center justify-between">
                          <p className="font-medium truncate pr-4">{item.projectName}</p>
                          <span className={`text-sm font-semibold ${item.successRate >= 70 ? "text-emerald-400" : "text-amber-400"}`}>{item.successRate}%</span>
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-sm">
                          <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-emerald-400">{item.passedRuns} passed</span>
                          <span className="rounded-full bg-rose-500/10 px-2 py-1 text-rose-400">{item.failedRuns} failed</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* FULL HISTORY TABLE */}
            <div className={`rounded-3xl border ${borderColor} ${cardBg} overflow-hidden shadow-sm`}>
              <div className="border-b border-gray-800/50 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Execution history</h2>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-slate-500"}`}>Recent automation runs and their outcomes</p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto max-h-[500px]">
                <table className="min-w-full text-left text-sm relative">
                  <thead className={`sticky top-0 z-10 ${darkMode ? "bg-[#0b1220] text-gray-400" : "bg-slate-100 text-slate-600"}`}>
                    <tr>
                      <th className="px-6 py-3 font-medium">Title</th>
                      <th className="px-6 py-3 font-medium">Project</th>
                      <th className="px-6 py-3 font-medium">Status</th>
                      <th className="px-6 py-3 font-medium">Duration</th>
                      <th className="px-6 py-3 font-medium">Executed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/60 dark:divide-gray-800/60">
                    {finalFilteredRuns.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                          <div className="flex flex-col items-center gap-2">
                            <XCircle size={20} />
                            No matching runs found.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      finalFilteredRuns.map((run) => {
                        const projectName = report.projectMap[String(run.project_id)]?.project_name || "Unknown";
                        return (
                          <tr key={`${run._id || run.title}-${run.created_at}`} className={`transition hover:bg-white/5 ${darkMode ? "border-gray-800/60" : "border-slate-200"}`}>
                            <td className="px-6 py-4">
                              <div className="font-medium max-w-xs truncate" title={run.title}>{run.title || "Automation run"}</div>
                              <div className={`text-xs mt-1 max-w-xs truncate ${darkMode ? "text-gray-500" : "text-slate-500"}`}>{run.target_url}</div>
                            </td>
                            <td className="px-6 py-4 max-w-[150px] truncate" title={projectName}>{projectName}</td>
                            <td className="px-6 py-4">
                              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${run.status === "Pass" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border border-rose-500/20"}`}>
                                {run.status || "Unknown"}
                              </span>
                            </td>
                            <td className="px-6 py-4 font-mono text-xs">{run.duration || "0s"}</td>
                            <td className="px-6 py-4 text-gray-500">{run.created_at ? new Date(run.created_at).toLocaleString() : "Unknown"}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}